import { useState, useEffect, useRef } from "react";
import ElementDetailModal from "./ElementDetail";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { getElements } from "../../Services/elements";
import DefaultLoader from "../../Pages/LoadingPage/DefaultLoader";
import { ReactComponent as AppStoreBadge } from "../../Assets/icon/Applebutton.svg";
import { ReactComponent as GooglePlayBadge } from "../../Assets/icon/Googlebutton.svg";
import "./style.css";
import "./mobile-styles.css"; // Import mobile-specific fixes

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" fill="none" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
    <path d="M12 11v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// Judgement is currently gated (DISABLED_DOMAINS) so it renders last
// after the available + upsell domains.
const DOMAIN_ORDER = ["Identity", "Cognition", "Resolution", "Motivation", "Judgement"];
const DOMAIN_DISPLAY = {
  Identity:   { label: "Identity",   tagline: "How You Show Up" },
  Cognition:  { label: "Cognition",  tagline: "How You Think" },
  Judgement:  { label: "Judgment",   tagline: "How You Make Decisions" },
  Resolution: { label: "Resolution", tagline: "How You Handle Conflict" },
  Motivation: { label: "Motives",    tagline: "What Drives You" },
};

// Aris assessment can only be completed in the mobile app, so empty domains
// link out to the stores instead of a "Start Now" web CTA. Judgment is gated
// (not yet released on either store) and renders without store buttons.
const APP_STORE_URL = "https://apps.apple.com/us/app/aris-your-guide-to-balance/id6744324334";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.aris.ai";
const DISABLED_DOMAINS = new Set(["Judgement"]);

const PeriodicTablePanel = ({
  width,
  categories,
  leftPanelWidth,
  activeTabs,
  groupBy = "Levels", // "Domains" | "Levels"
  onDomainHeaderClick,
  onDomainCarrotClick,
  leftRef,
  onSummariesLoaded,
  onLevelHeaderClick,
  onLevelCarrotClick,
  levelFocus,
  isMobile,
  showLevelCarrotFor,
  refreshSignal,
}) => {
  const [isElementModalOpen, setIsElementModalOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [element, setElement] = useState(null);
  const [filteredElement, setFilteredElement] = useState([]);
  const [allElements, setAllElements] = useState([]);
  const [adjustYourApproach, setAdjustYourApproach] = useState(null);
  const [elementCards, setElementCards] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [gridColumns, setGridColumns] = useState(5); // Default number of columns
  const [isLoading, setIsLoading] = useState(true);

  // Group elements by level
  const elementsByLevel = filteredElement?.length
    ? filteredElement.reduce((acc, element) => {
      const level = element.level || "Unknown";
      if (!acc[level]) acc[level] = [];
      acc[level].push(element);
      return acc;
    }, {})
    : {};

  // Group elements by domain (uses element.category, which mirrors the API's domain key)
  const elementsByDomain = filteredElement?.length
    ? filteredElement.reduce((acc, element) => {
      const domain = element.category || element.domain || "Unknown";
      if (!acc[domain]) acc[domain] = [];
      acc[domain].push(element);
      return acc;
    }, {})
    : {};

  const { setHeaderContent } = useHeader();

  useEffect(() => {
    setHeaderContent(false);
    const fetchElements = async () => {
      setIsLoading(true);
      try {
        const response = await getElements();
        const elementsOriginal = response?.payload?.elements_original;

        // New API structure: elements_original is an object with category arrays
        // e.g., { Identity: [...], Cognition: [], Judgment: [], Resolution: [], Motivation: [] }
        setElement(elementsOriginal);

        // The API may pre-render the per-element AI cards (showing-up-recently
        // / try-a-different-approach) inside `summaries.element_cards`. Cache
        // them so ElementDetailModal can use them directly and skip the lazy
        // fetch when content is already available.
        setElementCards(response?.payload?.summaries?.element_cards || null);

        // "Adjust Your Approach" — green priority section above Identity, only
        // visible when the API explicitly opts in.
        const adjust = response?.payload?.adjust_your_approach || null;
        setAdjustYourApproach(
          adjust && adjust.visible && Array.isArray(adjust.items) && adjust.items.length > 0
            ? adjust
            : null
        );

        // expose summaries, status, and elements data to parent to drive right panel
        if (onSummariesLoaded) {
          onSummariesLoaded({
            summaries: response?.payload?.summaries || null,
            summary_status: response?.payload?.summary_status || null,
            elements_data: elementsOriginal || null,
          });
        }
        
        // Flatten all elements from all categories for filtering
        const allElementsArray = [];
        if (elementsOriginal) {
          Object.keys(elementsOriginal).forEach(category => {
            if (Array.isArray(elementsOriginal[category])) {
              allElementsArray.push(...elementsOriginal[category]);
            }
          });
        }
        setAllElements(allElementsArray);
      } catch (error) {
        console.error("Error fetching elements:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchElements();
  }, [refreshSignal]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        height: window.innerHeight,
      });
      setIsMobileView(width <= 600);
    };

    handleResize(); // Initialize on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Effect to calculate optimal number of grid columns based on container width
  // and determine if we're on a small screen
  useEffect(() => {
    const calculateGridColumns = () => {
      if (!leftRef.current) return;

      // Get the width of the container
      const containerWidth = leftRef.current.offsetWidth;

      // Check if we're on a small screen or have a small left panel
      const isSmall = containerWidth <= 550 || leftPanelWidth <= 36;
      setIsSmallScreen(isSmall);

      // Element width + gap + padding
      // Matches Figma 14833:55983 Element Card width (117px) so column
      // count stays accurate after the card size bump.
      const elementWidth = 117; // Element width
      const elementGap = 16;    // Gap between elements
      const containerPadding = 32; // Container padding

      // Calculate how many elements can fit in a row with proper spacing
      const availableWidth = containerWidth - containerPadding;
      let columnsCount = Math.floor(availableWidth / (elementWidth + elementGap));

      // Set a minimum of 1 column and a maximum of 8 columns
      columnsCount = Math.max(1, Math.min(8, columnsCount));

      // For mobile view, limit to 4 columns
      if (window.innerWidth <= 600) {
        columnsCount = Math.min(4, columnsCount);
      }

      setGridColumns(columnsCount);
    };

    // Calculate on mount
    calculateGridColumns();

    // Set up a resize observer to recalculate when container size changes
    const resizeObserver = new ResizeObserver(() => {
      calculateGridColumns();
    });

    if (leftRef.current) {
      resizeObserver.observe(leftRef.current);
    }

    // Clean up the observer when component unmounts
    return () => {
      if (leftRef.current) {
        resizeObserver.unobserve(leftRef.current);
      }
    };
  }, [leftPanelWidth]);

  useEffect(() => {
    if (element) {
      // Flatten all elements from all categories and filter out UnderPressure
      const allElementsArray = [];
      Object.keys(element).forEach(category => {
        if (Array.isArray(element[category])) {
          const categoryElements = element[category].filter(
            (item) => item?.identityDomain !== "UnderPressure"
          );
          allElementsArray.push(...categoryElements);
        }
      });

      const isAllSelected = !activeTabs || activeTabs.length === 0 || activeTabs.includes("All");

      // For mobile view, only show elements in the selected categories
      // For desktop view, show all elements but apply visual filtering
      if (isMobileView && !isAllSelected) {
        const filteredElements = allElementsArray.filter(item => activeTabs.includes(item.category));
        setFilteredElement(filteredElements);
      } else {
        setFilteredElement(allElementsArray);
      }
    }
  }, [element, activeTabs, isMobileView]);

  // This function checks if an element belongs to any of the selected categories
  const isElementInActiveCategory = (elementItem) => {
    const isAllSelected = !activeTabs || activeTabs.length === 0 || activeTabs.includes("All");
    if (isAllSelected) return true;
    return activeTabs.includes(elementItem.category);
  };

  const handleElementClick = (element) => {
    setSelectedElement(element);
    setIsElementModalOpen(true);
  };

  // Renders a single element card. Extracted so the "Adjust Your Approach"
  // priority section can reuse the exact same visual treatment as the
  // domain/level grids below it.
  const renderElementCard = (_ele, keyPrefix, opts = {}) => {
    if (_ele?.isEmpty) {
      return <div key={`empty-${keyPrefix}`} className="aris-element empty-slot"></div>;
    }

    const badge = _ele?.label && _ele.label !== "" ? _ele.label : null;
    const badgeClass = badge ? `element-label-badge badge-${badge.toLowerCase()}` : "";

    return (
      <div
        key={`${_ele?.symbol}-${_ele?.id}-${keyPrefix}`}
        className={`aris-element ${opts.extraClass || ""}`}
        onClick={() => handleElementClick(_ele)}
      >
        <div className="aris-element-inner">
          {badge && (
            <span className={badgeClass}>
              {badge.charAt(0).toUpperCase() + badge.slice(1).toLowerCase()}
            </span>
          )}
          <div className="id-elements-container">
            <div className="id-elements">
              <span className="element-id-heading">ID</span>
              <span className="element-id-bullet">•</span>
              <span className="element-id-value">
                {String(_ele.id ?? "").padStart(2, "0")}
              </span>
            </div>
          </div>
          <div className="element-icon">
            <img src={_ele.element_url} alt={_ele.displayName} className="element-image" />
          </div>
          <div className="element-name-container">
            <div className="element-name">{_ele.displayName}</div>
          </div>
        </div>
      </div>
    );
  };

  // Resolve adjust_your_approach items (which carry only element_key + summary
  // metadata) into the full element record from elements_original so we can
  // render the same element card as the rest of the table.
  const adjustItems = (() => {
    if (!adjustYourApproach || !Array.isArray(adjustYourApproach.items)) return [];
    const byKey = new Map();
    allElements.forEach((el) => {
      if (el?.key && !byKey.has(el.key)) byKey.set(el.key, el);
    });
    return adjustYourApproach.items
      .map((item) => {
        const full = byKey.get(item.element_key);
        return full || null;
      })
      .filter(Boolean);
  })();

  // Removed fixed height calculation to allow content to determine height

  return (
    <div
      style={{
        width: "100%",
        height: "100%", // Use 100% to fill the parent container
        display: "flex",
        flexDirection: "column"
      }}
    >
      <div className="aris-periodic-table">
        <div
          className={`aris-element-grid ${isMobileView ? 'mobile-horizontal-scroll' : ''} aris-element-grid-no-scroll`}
          ref={leftRef}
          style={{ height: "auto" }}
        >
          {isLoading ? (
            <div className="element-loader-container">
              <DefaultLoader />
            </div>
          ) : (filteredElement.length > 0 || groupBy === "Domains") ? (
            <>
              {adjustYourApproach && adjustItems.length > 0 && (
                <div className="aris-adjust-approach" role="region" aria-label={adjustYourApproach.title}>
                  <div className="aris-adjust-approach-text">
                    <h3 className="aris-adjust-approach-title">{adjustYourApproach.title}</h3>
                    {adjustYourApproach.subcopy && (
                      <p className="aris-adjust-approach-subcopy">{adjustYourApproach.subcopy}</p>
                    )}
                  </div>
                  <div className="aris-adjust-approach-cards">
                    {adjustItems.map((ele, index) =>
                      renderElementCard(ele, `adjust-${ele?.key || index}`)
                    )}
                  </div>
                </div>
              )}
              {(() => {
              const sections =
                groupBy === "Domains"
                  ? DOMAIN_ORDER.map((domainKey) => ({
                      kind: "domain",
                      key: domainKey,
                      label: DOMAIN_DISPLAY[domainKey]?.label || domainKey,
                      tagline: DOMAIN_DISPLAY[domainKey]?.tagline || "",
                      list: elementsByDomain[domainKey] || [],
                    }))
                  : ["Primary", "Active", "Moderate", "Subtle"].map((levelName) => ({
                      kind: "level",
                      key: levelName,
                      label: `${levelName} Aris Elements`,
                      tagline: "",
                      list: elementsByLevel[levelName] || [],
                    }));

              return sections.map((section) => {
                const isEmpty = !section.list || section.list.length === 0;
                // Levels collapse when empty; domains always render so the
                // user can see every domain with a placeholder/store CTA.
                if (isEmpty && section.kind !== "domain") return null;

                if (isEmpty && section.kind === "domain") {
                  const isDisabled = DISABLED_DOMAINS.has(section.key);
                  return (
                    <div
                      key={`${section.kind}-${section.key}`}
                      className={`level-container domain-placeholder ${isDisabled ? "domain-placeholder-disabled" : ""}`}
                    >
                      <h3 className="domain-placeholder-title">{section.label}</h3>
                      <p className="domain-placeholder-body">
                        {`Complete “${section.tagline}” in order to add these elements`}
                      </p>
                      {!isDisabled && (
                        <div className="domain-placeholder-stores">
                          <a
                            className="domain-placeholder-store"
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download on the App Store"
                          >
                            <AppStoreBadge />
                          </a>
                          <a
                            className="domain-placeholder-store"
                            href={PLAY_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Get it on Google Play"
                          >
                            <GooglePlayBadge />
                          </a>
                        </div>
                      )}
                    </div>
                  );
                }

                const onHeaderClick = () => {
                  if (section.kind === "domain") {
                    onDomainHeaderClick && onDomainHeaderClick(section.label);
                  } else {
                    onLevelHeaderClick && onLevelHeaderClick(section.key);
                  }
                };
                const onInfoClick = (e) => {
                  e.stopPropagation();
                  if (section.kind === "domain") {
                    onDomainCarrotClick && onDomainCarrotClick(section.label);
                  } else {
                    onLevelCarrotClick && onLevelCarrotClick(section.key);
                  }
                };

                return (
                  <div key={`${section.kind}-${section.key}`} className="level-container">
                    <div
                      className={`level-header ${section.kind === "domain" ? "domain-header" : ""}`}
                      onClick={onHeaderClick}
                    >
                      <h3 className="level-title">
                        {section.kind === "domain" ? (
                          <>
                            {section.label}
                            {section.tagline && (
                              <>
                                <span className="domain-divider"> | </span>
                                <span className="domain-tagline">{section.tagline}</span>
                              </>
                            )}
                          </>
                        ) : (
                          section.label
                        )}
                      </h3>
                      <div
                        className="info-icon-container"
                        title="Learn more"
                        style={{ cursor: "pointer" }}
                        onClick={onInfoClick}
                      >
                        <InfoIcon />
                      </div>
                    </div>
                    <div
                      className="level-elements"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        alignContent: "center",
                        gap: "18px",
                        flexWrap: "wrap",
                        width: "100%",
                      }}
                    >
                      {section.list.map((_ele, index) => {
                        if (_ele?.isEmpty) {
                          return (
                            <div
                              key={`empty-${section.key}-${index}`}
                              className="aris-element empty-slot"
                            ></div>
                          );
                        }

                        const isLastRow =
                          Math.floor(index / gridColumns) ===
                          Math.floor((section.list.length - 1) / gridColumns);

                        const isInactiveLevel =
                          section.kind === "level" && levelFocus && section.key !== levelFocus;
                        const isFocusedLevel =
                          section.kind === "level" && levelFocus && section.key === levelFocus;

                        const badge = _ele?.label && _ele.label !== "" ? _ele.label : null;
                        const badgeClass = badge
                          ? `element-label-badge badge-${badge.toLowerCase()}`
                          : "";

                        return (
                          <div
                            key={`${_ele?.symbol}-${_ele?.id}-${section.key}-${index}`}
                            className={`aris-element ${isInactiveLevel ? "inactive-level" : ""} ${
                              isFocusedLevel ? "focused-level" : ""
                            } ${!isSmallScreen && isLastRow ? "last-row-element" : ""} ${
                              !activeTabs.includes("All") ? "element-filter-active" : ""
                            }`}
                            onClick={() => handleElementClick(_ele)}
                            data-row={Math.floor(index / gridColumns)}
                            data-is-last-row={isLastRow}
                            data-level={section.kind === "level" ? section.key.toLowerCase() : undefined}
                          >
                            <div
                              className={`aris-element-inner ${
                                !(!activeTabs || activeTabs.length === 0 || activeTabs.includes("All"))
                                  ? isElementInActiveCategory(_ele)
                                    ? "active-category"
                                    : "inactive-category"
                                  : ""
                              }`}
                            >
                              {badge && (
                                <span className={badgeClass}>
                                  {badge.charAt(0).toUpperCase() + badge.slice(1).toLowerCase()}
                                </span>
                              )}
                              <div className="id-elements-container">
                                <div className="id-elements">
                                  <span className="element-id-heading">ID</span>
                                  <span className="element-id-bullet">•</span>
                                  <span className="element-id-value">
                                    {String(_ele.id ?? "").padStart(2, "0")}
                                  </span>
                                </div>
                              </div>
                              <div className="element-icon">
                                <img
                                  src={_ele.element_url}
                                  alt={_ele.displayName}
                                  className="element-image"
                                />
                              </div>
                              <div className="element-name-container">
                                <div className="element-name">{_ele.displayName}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
            </>
          ) : (
            <div>No elements found for this category</div>
          )}
        </div>
      </div>

      <ElementDetailModal
        isOpen={isElementModalOpen}
        onClose={() => setIsElementModalOpen(false)}
        element={selectedElement}
        preloadedCards={
          selectedElement?.key && elementCards
            ? elementCards[selectedElement.key] || null
            : null
        }
      />
    </div>
  );
};

export default PeriodicTablePanel;
